import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { categories, editorialStories, testimonials } from '../mock';
import ProductCard from '../components/ProductCard';
import api from '../api';

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get('/products').then(r => setProducts(r.data)).catch(() => {});
  }, []);

  const featured = products.slice(0, 4);
  const desk = products.filter(p => p.category === 'desk').slice(0, 3);

  return (
    <main>
      <section className="relative overflow-hidden">
        <div className="grid md:grid-cols-2 min-h-[80vh] md:min-h-[88vh]">
          <div className="flex flex-col justify-center px-6 md:px-16 py-16 order-2 md:order-1">
            <p className="text-[11px] tracking-widest-plus uppercase text-[#C6410E] mb-6 fade-up">The Autumn Edit — 2025</p>
            <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] mb-8 fade-up fade-up-delay-1">
              Objects, made<br />
              with <em className="text-[#C6410E]">patience</em>.
            </h1>
            <p className="text-base md:text-lg text-[#1a1a1a]/70 max-w-md mb-10 leading-relaxed fade-up fade-up-delay-2">
              A quiet collection of everyday leather goods. Cut, stitched and finished by hand, in a small workshop we know by name.
            </p>
            <div className="flex flex-wrap gap-4 fade-up fade-up-delay-3">
              <Link to="/shop" className="btn-primary">Discover the Collection</Link>
              <Link to="/our-story" className="btn-ghost">Our Story</Link>
            </div>
          </div>
          <div className="order-1 md:order-2 relative min-h-[50vh] md:min-h-full bg-[#E9E1D4]">
            <img src="https://images.pexels.com/photos/27046146/pexels-photo-27046146.jpeg" alt="Leather laptop portfolio" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="border-y border-black/10 py-4 overflow-hidden bg-[#F4EEE4]">
        <div className="flex marquee-track whitespace-nowrap gap-16 text-[11px] tracking-widest-plus uppercase">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex gap-16 shrink-0">
              <span>— Handcrafted in India</span><span>·</span>
              <span>Vegetable-tanned full-grain leather</span><span>·</span>
              <span>Complimentary monogramming</span><span>·</span>
              <span>Repair, not replace</span><span>·</span>
              <span>Since 2018</span><span>·</span>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex items-end justify-between mb-14">
          <div>
            <p className="text-[11px] tracking-widest-plus uppercase text-[#C6410E] mb-3">The Collections</p>
            <h2 className="font-serif-display text-4xl md:text-5xl max-w-xl leading-tight">A place for every object</h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 text-[11px] tracking-widest-plus uppercase link-underline">
            All Collections <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {categories.map((c, idx) => (
            <Link key={c.id} to={`/shop/${c.id}`} className={`group relative overflow-hidden ${idx === 0 || idx === 3 ? 'md:col-span-2 aspect-square md:aspect-auto' : 'aspect-[4/5]'}`}>
              <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-[#F4EEE4]">
                <p className="text-[10px] tracking-widest-plus uppercase mb-2 opacity-80">{c.count} pieces</p>
                <h3 className="font-serif-display text-2xl md:text-4xl">{c.name}</h3>
                <p className="text-xs md:text-sm opacity-90 mt-1">{c.subtitle}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="flex items-end justify-between mb-14">
          <div>
            <p className="text-[11px] tracking-widest-plus uppercase text-[#C6410E] mb-3">Newly Made</p>
            <h2 className="font-serif-display text-4xl md:text-5xl">This Season</h2>
          </div>
          <Link to="/shop" className="text-[11px] tracking-widest-plus uppercase link-underline">See All</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {featured.map(p => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      <section className="bg-[#1a1a1a] text-[#F4EDDF]">
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32 grid md:grid-cols-2 gap-14 items-center">
          <div className="aspect-[4/5] overflow-hidden">
            <img src="https://images.pexels.com/photos/11676700/pexels-photo-11676700.jpeg" alt="Artisan hands stitching leather" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="text-[11px] tracking-widest-plus uppercase text-[#E8A76B] mb-6">The House</p>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-tight mb-8">Eleven pairs of hands. One belt.</h2>
            <p className="text-base md:text-lg opacity-80 leading-relaxed mb-6">
              Every barebasics piece is the work of a small team who know each other by first name. We choose the hide, cut it by pattern, edge it, saddle-stitch it, and finish it by hand.
            </p>
            <p className="text-base md:text-lg opacity-80 leading-relaxed mb-10">
              We are not fast, and we do not intend to be. What we make is meant to be used, and to age with you.
            </p>
            <Link to="/our-story" className="btn-ghost" style={{ borderColor: '#F4EDDF', color: '#F4EDDF' }}>Read our story</Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-3 gap-10 mb-14">
          <div className="md:col-span-1">
            <p className="text-[11px] tracking-widest-plus uppercase text-[#C6410E] mb-3">Feature</p>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mb-6">The Desk<br/>Collection</h2>
            <p className="text-[#1a1a1a]/70 leading-relaxed mb-6">
              Mouse pads, coasters, tissue box covers and other quiet objects. Made for the room where you spend most of your day.
            </p>
            <Link to="/shop/desk" className="text-[11px] tracking-widest-plus uppercase link-underline inline-flex items-center gap-2">Shop the Desk <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {desk.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="flex items-end justify-between mb-14">
          <h2 className="font-serif-display text-4xl md:text-5xl">The Journal</h2>
          <Link to="/our-story" className="text-[11px] tracking-widest-plus uppercase link-underline">Read All</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {editorialStories.map(s => (
            <article key={s.id} className="group">
              <div className="product-img-wrap aspect-[3/2] mb-5">
                <img src={s.image} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <p className="text-[11px] tracking-widest-plus uppercase text-[#C6410E] mb-2">{s.kicker}</p>
              <h3 className="font-serif-display text-2xl md:text-3xl leading-tight mb-3 group-hover:text-[#C6410E] transition-colors">{s.title}</h3>
              <p className="text-[#1a1a1a]/70 leading-relaxed">{s.excerpt}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-[#E9E1D4]">
        <div className="max-w-5xl mx-auto px-6 py-24 text-center">
          <p className="text-[11px] tracking-widest-plus uppercase text-[#C6410E] mb-6">Notes from the studio</p>
          <div className="grid md:grid-cols-3 gap-10">
            {testimonials.map(t => (
              <div key={t.id}>
                <p className="font-serif-display text-2xl leading-snug mb-5 italic">&ldquo;{t.quote}&rdquo;</p>
                <p className="text-[11px] tracking-widest-plus uppercase text-[#1a1a1a]/60">{t.author} — {t.city}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
